import { useCallback, useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { Button, Card, EmptyState, Field, Input, Select, useToast } from "../../ui";
import { HubListGroup, HubRow } from "../AdminPortal/HubPwa";
import s from "../clinic.module.css";
import aa from "../AdminPortal/adminApp.module.css";

function emptyMailingDraft() {
  return {
    title: "",
    body: "",
    target_type: "all",
    target_group_ids: [],
  };
}

function MailingEditor({ draft, onChange, groups, onClose, onSave, saving, isApp }) {
  const set = (patch) => onChange({ ...draft, ...patch });

  const toggleGroup = (id) => {
    const gid = String(id);
    const has = draft.target_group_ids.includes(gid);
    set({
      target_group_ids: has
        ? draft.target_group_ids.filter((x) => x !== gid)
        : [...draft.target_group_ids, gid],
    });
  };

  const body = (
    <>
      <Field label="Заголовок push-уведомления">
        <Input
          value={draft.title}
          onChange={(e) => set({ title: e.target.value })}
          placeholder="Например: Перенос занятия"
        />
      </Field>
      <Field label="Текст сообщения">
        <textarea
          className={s.pollTextarea}
          rows={4}
          value={draft.body}
          onChange={(e) => set({ body: e.target.value })}
          placeholder="Текст, который увидят клиенты"
        />
      </Field>
      <Field label="Кому отправить">
        <Select
          value={draft.target_type}
          onChange={(e) =>
            set({ target_type: e.target.value, target_group_ids: [] })
          }
        >
          <option value="all">Все клиенты клуба</option>
          <option value="groups">Выбранные группы</option>
        </Select>
      </Field>
      {draft.target_type === "groups" ? (
        <div className={s.pollGroupPick}>
          {groups.length === 0 ? (
            <p className={s.clientTabMuted}>Групп пока нет</p>
          ) : (
            groups.map((g) => (
              <label key={g.id} className={s.pollGroupChip}>
                <input
                  type="checkbox"
                  checked={draft.target_group_ids.includes(String(g.id))}
                  onChange={() => toggleGroup(g.id)}
                />
                {g.name}
              </label>
            ))
          )}
        </div>
      ) : null}
      <div className={isApp ? aa.hubEditorActions : s.pollsCardActions}>
        <Button type="button" variant="secondary" onClick={onClose}>
          Отмена
        </Button>
        <Button type="button" onClick={onSave} disabled={saving}>
          {saving ? "Сохранение…" : "Сохранить черновик"}
        </Button>
      </div>
    </>
  );

  if (isApp) {
    return (
      <div className={aa.hubEditor}>
        <div className={aa.hubEditorHead}>
          <h2>Новая рассылка</h2>
          <button type="button" className={aa.hubEditorClose} onClick={onClose}>
            ×
          </button>
        </div>
        {body}
      </div>
    );
  }

  return (
    <div className={s.pollsEditorBackdrop} onClick={onClose} role="presentation">
      <aside
        className={`${s.pollsEditor} ${s.pollsEditor_wide}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
      >
        <header className={s.pollsEditorHead}>
          <h3>Новая рассылка</h3>
          <button type="button" className={s.pollsEditorClose} onClick={onClose}>
            ×
          </button>
        </header>
        <div className={s.pollsEditorBody}>{body}</div>
      </aside>
    </div>
  );
}

export default function MailingsPanel({ groups, editorOpen, onEditorOpenChange }) {
  const toast = useToast();
  const isApp = useAdminHubUi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(emptyMailingDraft);
  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await clinicAPI.getBroadcasts();
      setRows(data.broadcasts || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка загрузки");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const closeEditor = () => {
    onEditorOpenChange(false);
    setDraft(emptyMailingDraft());
  };

  const saveDraft = async () => {
    if (!draft.title.trim() || !draft.body.trim()) {
      toast.error("Заполните заголовок и текст");
      return;
    }
    setSaving(true);
    try {
      await clinicAPI.createBroadcast(draft);
      toast.success("Черновик сохранён");
      closeEditor();
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const send = async (id) => {
    if (!window.confirm("Отправить push всем получателям?")) return;
    setSendingId(id);
    try {
      const r = await clinicAPI.sendBroadcast(id);
      toast.success(
        `Отправлено: ${r.push?.sent ?? 0} из ${r.broadcast?.recipients_count ?? "?"}`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка отправки");
    } finally {
      setSendingId(null);
    }
  };

  return (
    <>
      {editorOpen ? (
        <MailingEditor
          draft={draft}
          onChange={setDraft}
          groups={groups}
          onClose={closeEditor}
          onSave={saveDraft}
          saving={saving}
          isApp={isApp}
        />
      ) : null}

      {loading ? (
        <Card>
          <p className={s.pollsEditorIntro}>Загрузка…</p>
        </Card>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Рассылок пока нет"
          description="Push-сообщения без опроса: объявления, переносы, напоминания"
          actionLabel="Создать рассылку"
          onAction={() => onEditorOpenChange(true)}
        />
      ) : isApp ? (
        <HubListGroup>
          {rows.map((b) => (
            <HubRow
              key={b.id}
              title={b.title}
              subtitle={
                b.status === "sent"
                  ? `Отправлено · ${b.recipients_count} получ.`
                  : "Черновик"
              }
              meta={b.body?.slice(0, 60)}
              trailing={
                b.status === "draft" ? (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => send(b.id)}
                    disabled={sendingId === b.id}
                  >
                    {sendingId === b.id ? "…" : "Отправить"}
                  </Button>
                ) : (
                  <span className={aa.hubRowBadge}>✓</span>
                )
              }
            />
          ))}
        </HubListGroup>
      ) : (
        <ul className={s.pollsCardList}>
          {rows.map((b) => (
            <li key={b.id}>
              <Card className={s.pollsCard}>
                <div className={s.pollsCardTop}>
                  <div>
                    <span className={s.pollsTypeBadge}>Рассылка</span>
                    <h3 className={s.pollsCardTitle}>{b.title}</h3>
                  </div>
                </div>
                <p className={s.pollsCardMeta}>{b.body}</p>
                <p className={s.pollsCardMeta}>
                  {b.status === "sent"
                    ? `Отправлено · ${b.recipients_count} получателей`
                    : "Черновик"}
                </p>
                {b.status === "draft" ? (
                  <div className={s.pollsCardActions}>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => send(b.id)}
                      disabled={sendingId === b.id}
                    >
                      {sendingId === b.id ? "Отправка…" : "Отправить push"}
                    </Button>
                  </div>
                ) : null}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
